/**
 * ゆめさくコースを 24 の分野に振り分ける。
 *
 * バッジは分野ごとに 1 枚しかない。101 本のコースをどの分野に数えるかは
 * ここだけで決める。画面ごとに振り分けると、同じコースが 2 つの分野で
 * 数えられて、合計がコース数と合わなくなる。
 *
 * 振り分けは slug の頭で見る。DB の category / subcategory を使わないのは、
 * あちらが「フロントエンド」「バックエンド」のような 5 つの棚で、
 * 分野の 24 枚とは粒度が違うため。
 */

export type Field = {
  /** 絵の名前。app/lib/badges/field-art/ のファイル名と同じ */
  slug: string;
  title: string;
};

export const FIELDS: readonly Field[] = [
  { slug: "01-html-css", title: "HTML/CSS" },
  { slug: "02-javascript", title: "JavaScript" },
  { slug: "03-typescript", title: "TypeScript" },
  { slug: "04-react", title: "React" },
  { slug: "05-nextjs", title: "Next.js" },
  { slug: "06-nodejs", title: "Node.js" },
  { slug: "07-go", title: "Go" },
  { slug: "08-java", title: "Java" },
  { slug: "09-python", title: "Python" },
  { slug: "10-c", title: "C言語" },
  { slug: "11-sql-db", title: "SQL・データベース" },
  { slug: "12-linux", title: "Linux" },
  { slug: "13-server-infra", title: "サーバー・インフラ" },
  { slug: "14-aws", title: "AWS" },
  { slug: "15-cs", title: "コンピュータサイエンス" },
  { slug: "16-data-analysis", title: "データ分析" },
  { slug: "17-ai-use", title: "AI活用" },
  { slug: "18-claude-code", title: "Claude Code" },
  { slug: "19-vibe-coding", title: "バイブコーディング" },
  { slug: "20-ai-app", title: "AIアプリ開発" },
  { slug: "21-git", title: "Git" },
  { slug: "22-docker", title: "Docker" },
  { slug: "23-career", title: "キャリア" },
  { slug: "24-exam", title: "資格試験" },
];

/**
 * 頭の一致では決められないコース。
 *
 * 資格のコースは、題材が 1 つの技術に閉じているならその技術に数える。
 * java-silver は Java、aws-clf は AWS、linuc-l1 は Linux。24-exam に
 * 入れるのは、題材が特定の技術に閉じていない試験だけ (基本情報・IT パスポート)。
 * 全部 24-exam に寄せると、Java を 5 本終えた人の Java が 4 本に見える。
 */
const EXPLICIT: Record<string, string> = {
  // 道場。名前から技術が読めないものがある
  "dojo-fd": "02-javascript", // フロントデバッグ道場
  "dojo-hc": "01-html-css", // HTML/CSS道場
  "dojo-ex": "06-nodejs", // Express道場
  "dojo-pr": "17-ai-use", // プロンプト道場
  "dojo-re": "04-react", // React道場
  "dojo-algo": "15-cs",
  "dojo-js": "02-javascript",
  "dojo-ts": "03-typescript",
  "dojo-python": "09-python",
  "dojo-java": "08-java",
  "dojo-go": "07-go",
  "dojo-sql": "11-sql-db",
  "dojo-lx": "12-linux",

  // 題材が 1 つの技術に閉じている資格
  "java-silver": "08-java",
  "linuc-l1": "12-linux",
  "aws-clf": "14-aws",
  "aws-saa": "14-aws",

  // 頭が他の分野と重なるもの
  "cs-theory": "15-cs",
  "cs-algorithms": "15-cs",
  "cs-system-design": "15-cs",
  "career-path-guide": "23-career",
  "programming-learning-guide": "23-career",
  "spring-boot-basics": "08-java",
  "server-basics": "13-server-infra",
  "data-analysis": "16-data-analysis",
  "db-design-basics": "11-sql-db",
  "sql-basics": "11-sql-db",
  "rag-basics": "20-ai-app",
  "vibe-coding": "19-vibe-coding",
  "responsive-web": "01-html-css",
  "prompt-engineering": "17-ai-use",
  "gene-ai": "17-ai-use",
  "generative-ai-basics": "17-ai-use",
  "ai-engineer-trial": "17-ai-use",
  "fe-exam": "24-exam",
};

/**
 * slug の頭で見る規則。**上から順に見て、最初に当たったものを採る。**
 *
 * 並びが命。"javascript-" は "java" より先に置く。逆にすると
 * JavaScript のコースが全部 Java に入る。同じ理由で "css-" と "cs-" を
 * "c-" より先に置く。
 */
const PREFIX_RULES: readonly [string, string][] = [
  ["javascript-", "02-javascript"],
  ["typescript-", "03-typescript"],
  ["java-", "08-java"],
  ["nextjs-", "05-nextjs"],
  ["nodejs", "06-nodejs"],
  ["react-", "04-react"],
  ["python-", "09-python"],
  ["go-", "07-go"],
  ["css-", "01-html-css"],
  ["html-", "01-html-css"],
  ["cs-", "15-cs"],
  ["claude-code", "18-claude-code"],
  ["c-", "10-c"],
  ["linux-", "12-linux"],
  ["aws-", "14-aws"],
  ["it-passport", "24-exam"],
];

/** そのコースがどの分野か。分からなければ null */
export function fieldOfCourseSlug(slug: string): string | null {
  const explicit = EXPLICIT[slug];
  if (explicit) return explicit;
  for (const [prefix, field] of PREFIX_RULES) {
    if (slug.startsWith(prefix)) return field;
  }
  return null;
}
